import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import { getPointOfSales } from '../../../services/pointOfSalesService';
import { ventasReportService } from '../../../services/ventasReportService';
import VentasSalesDashboardView from './SalesDashboardView';

const STORAGE_KEY = 'ventas_reports_sales_overview_filters';

function defaultDates() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultDates(), pointOfSaleId: '' };
    const parsed = JSON.parse(raw);
    return {
      dateFrom: parsed.dateFrom || defaultDates().dateFrom,
      dateTo: parsed.dateTo || defaultDates().dateTo,
      pointOfSaleId: parsed.pointOfSaleId || '',
    };
  } catch {
    return { ...defaultDates(), pointOfSaleId: '' };
  }
}

function saveStoredFilters(filters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

export default function VentasSalesDashboard() {
  const [filters, setFilters] = useState(loadStoredFilters);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getPointOfSales()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setPointsOfSale(res.data.filter((p) => p.is_active !== false));
        }
      })
      .catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      };
      if (filters.pointOfSaleId) {
        params.point_of_sale_id = filters.pointOfSaleId;
      }
      const res = await ventasReportService.getSalesOverview(params);
      if (res.success) {
        setData(res.data);
        setMeta(res.meta);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el dashboard de ventas',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1500px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">
              Dashboard
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/orders" className="hover:text-teal-600">
              Ventas
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Dashboard de ventas</span>
          </nav>

          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-800">Dashboard de ventas</h1>
            <p className="text-gray-600 mt-1 max-w-3xl">
              Totales, ticket promedio, medios de pago, evolución diaria y ranking de productos.
              Sin costos ni márgenes.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Punto de venta</label>
                <select
                  value={filters.pointOfSaleId}
                  onChange={(e) => updateFilter('pointOfSaleId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos los POS</option>
                  {pointsOfSale.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.code} — {pos.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={loadData}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
                >
                  {loading ? 'Cargando…' : 'Actualizar'}
                </button>
              </div>
            </div>
            {meta ? (
              <p className="text-xs text-gray-500">
                Período {meta.date_from} → {meta.date_to}
                {meta.point_of_sale_id ? ' · filtrado por POS' : ' · todos los POS'}
              </p>
            ) : null}
          </div>

          {loading && !data ? (
            <div className="bg-white rounded-lg shadow-md p-16 text-center text-gray-500">
              Cargando dashboard…
            </div>
          ) : (
            <VentasSalesDashboardView data={data} filteredByPos={Boolean(filters.pointOfSaleId)} />
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
}
